import { useEffect, useMemo } from "react"
import { GeoJsonLayer } from "@deck.gl/layers"
import type { Feature } from "geojson"
import useSWR from "swr"
import { z } from "zod"
import { LayerStatus } from "./layerStatus"
import { useReportOverlayStatus } from "./useReportOverlayStatus"

const featureCollectionSchema = z.object({
    type: z.literal("FeatureCollection"),
    features: z.array(z.record(z.string(), z.unknown())),
})

const REFRESH_MS = 30 * 60 * 1000

// US: WFIGS interagency fire perimeters (public ArcGIS feature service, current year)
const WFIGS_URL =
    "https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/WFIGS_Interagency_Perimeters_Current/FeatureServer/0/query" +
    "?where=1%3D1&outFields=*&f=geojson&outSR=4326"

// Canada: CWFIS active fire perimeters (GeoServer WFS)
const CWFIS_URL =
    "https://cwfis.cfs.nrcan.gc.ca/geoserver/ows?service=WFS&version=2.0.0&request=GetFeature" +
    "&typeName=m3_polygons_current&outputFormat=application%2Fjson&srsName=EPSG%3A4326"

function parseFeatures(json: unknown): Feature[] {
    const parsed = featureCollectionSchema.safeParse(json)
    return parsed.success ? (parsed.data.features as unknown as Feature[]) : []
}

export async function fetchPerimeters() {
    const [us, ca] = await Promise.allSettled([
        fetch(WFIGS_URL).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
        fetch(CWFIS_URL).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
    ])

    const features: Feature[] = [
        ...(us.status === "fulfilled" ? parseFeatures(us.value) : []),
        ...(ca.status === "fulfilled" ? parseFeatures(ca.value) : []),
    ]

    if (us.status === "rejected") console.warn("[perimeters] WFIGS failed:", us.reason)
    if (ca.status === "rejected") console.warn("[perimeters] CWFIS failed:", ca.reason)

    return features
}

export function useFirePerimetersLayer(enabled: boolean) {
    const {
        data: features,
        error,
        isLoading,
    } = useSWR(enabled ? "fire-perimeters" : null, fetchPerimeters, {
        refreshInterval: REFRESH_MS,
        revalidateOnFocus: false,
    })

    useEffect(() => {
        if (error) console.error(error)
    }, [error])

    const status: LayerStatus = !enabled
        ? "idle"
        : error
          ? "error"
          : isLoading
            ? "loading"
            : "ready"

    useReportOverlayStatus("firePerimeters", status)

    return useMemo(() => {
        if (!enabled || !features) return null

        return new GeoJsonLayer({
            id: "fire-perimeters",
            data: { type: "FeatureCollection", features },
            stroked: true,
            filled: true,
            getFillColor: [255, 100, 20, 40],
            getLineColor: [255, 80, 0, 200],
            lineWidthMinPixels: 1.5,
            pickable: false,
        })
    }, [enabled, features])
}
